import type { Option } from '@ksyos/design-system';
import { Box, Button, DatePicker, PageHeader, Select, TextInput } from '@ksyos/design-system';
import { useEffect } from 'react';
import { ThirdPartyMessageStatus } from '../../../../common/enums/thirdPartyMessageStatus';
import { ThirdPartyMessageType } from '../../../../common/enums/thirdPartyMessageType';
import { translateKey } from '../../../translate';
import { useStateWithPartialSet } from '../../../utils/hooks/useStateWithPartialSet';
import { getCleanThirdPartyMessagesSearchState } from './ThirdPartyMessagesList';

export interface IThirdPartyMessagesListSearchState {
    firstScheduledDateTime: Date | null;
    itemsPerPage: number;
    messageSubtype: string;
    messageType: Option | null;
    page: number;
    status: Option | null;
}

interface IThirdPartyMessagesListSearchHeaderProps {
    clearSearchState: () => void;
    handleSearch: (partialNewSearchState: Partial<IThirdPartyMessagesListSearchState>) => void;
    searchState: IThirdPartyMessagesListSearchState;
}

export function ThirdPartyMessagesListSearchHeader(props: IThirdPartyMessagesListSearchHeaderProps) {
    const [searchState, setSearchState] = useStateWithPartialSet<IThirdPartyMessagesListSearchState>(
        getCleanThirdPartyMessagesSearchState()
    );

    useEffect(() => {
        setSearchState(props.searchState);
    }, [props.searchState]);

    return (
        <>
            <PageHeader title={translateKey('logs.thirdPartyMessages')} />
            <Box
                display="grid"
                gap={3}
                gridTemplateColumns={[
                    '1fr',
                    null,
                    '1fr max-content 1fr',
                    '200px 1fr 1fr max-content max-content max-content',
                ]}
                mb={4}
            >
                <Select
                    onChange={(item) => {
                        setSearchState({ status: item ?? null });
                    }}
                    options={Object.values(ThirdPartyMessageStatus).map((status) => ({
                        label: status.toString(),
                        value: status,
                    }))}
                    placeholder={translateKey('processThirdPartyMessages.status')}
                    value={searchState.status}
                />
                <Select
                    onChange={(item) => {
                        setSearchState({ messageType: item ?? null });
                    }}
                    options={Object.values(ThirdPartyMessageType).map((type) => ({
                        label: type.toString(),
                        value: type,
                    }))}
                    placeholder={translateKey('processThirdPartyMessages.messageType')}
                    value={searchState.messageType}
                />
                <TextInput
                    id="search"
                    onChange={(e) => setSearchState({ messageSubtype: e.target.value ? e.target.value : undefined })}
                    placeholder={translateKey('processThirdPartyMessages.messageSubtype')}
                    type="text"
                    value={searchState.messageSubtype}
                />
                <DatePicker
                    onChange={(value) => setSearchState({ firstScheduledDateTime: value })}
                    value={searchState.firstScheduledDateTime}
                />
                <Button onClick={() => props.handleSearch(searchState)}>{translateKey('common.search')}</Button>
                <Button
                    onClick={props.clearSearchState}
                    variant="outline"
                >
                    {translateKey('common.showAll')}
                </Button>
            </Box>
        </>
    );
}
